import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CatalogoSolicitudesService } from 'src/app/services/integracion-pos/catalogo-solicitudes.service';
import { ComercioActivoService } from 'src/app/services/merchant/comercio-activo.service';
import {
  ComercioFlujo, FlujoPorOmision, NodoRecorrido, PasoCatalogo, TipoSolicitud,
  claseDecide, comerciosQueSeApartanEn, construirRecorrido, diferenciaEnPalabras, formatoFecha,
  haceElPaso, iconoDecide, nombrePaso, pasosVisibles, seAparta, sentidoDecide, textoDecide,
} from '../tipos-flujos.dominio';

/**
 * EL DETALLE DE UN TIPO DE SOLICITUD — `/console/tipos-flujos/:code`.
 *
 * 🔴 Existe porque lo pidió Juan con estas palabras: *«cuando le des clic, poné componente de
 * detalle»*. Y porque es la regla de la casa: **cada detalle va en su propio componente, con su
 * propia ruta, a ancho completo. Nada de modales y nada de acordeones.**
 *
 * 🔴 AQUÍ NO HAY DIBUJO, y es una decisión, no un olvido: un tipo tiene uno o dos pasos, y un
 * dibujo de un paso es un adorno. La comparación —lo único que un dibujo cuenta mejor— está en la
 * portada, donde los cuatro tipos se ven sobre el mismo eje. Aquí va lo que un dibujo NO puede
 * decir: qué es cada paso, quién lo decide y qué comercio se aparta.
 *
 * El filtro de comercio de la cabecera también manda aquí: con un comercio elegido, los pasos son
 * los de ESE comercio.
 */
@Component({
  selector: 'app-tipo-flujo-detalle',
  templateUrl: './tipo-flujo-detalle.component.html',
  styleUrls: ['./tipo-flujo-detalle.component.css'],
})
export class TipoFlujoDetalleComponent implements OnInit, OnDestroy {

  cargando = true;
  error = '';
  code = '';

  tipo: TipoSolicitud | null = null;
  tipos: TipoSolicitud[] = [];
  pasos: PasoCatalogo[] = [];
  comercios: ComercioFlujo[] = [];
  flujoPorOmision: FlujoPorOmision | null = null;

  comercioCodigo: string | null = null;
  comercioConFlujo: ComercioFlujo | null = null;

  /** El recorrido de este tipo y lo que NO le pide al cliente. Campos, no getters: un arreglo
   *  nuevo en cada ciclo de detección es trabajo de más en cada movimiento del mouse. */
  recorrido: NodoRecorrido[] = [];
  fuera: PasoCatalogo[] = [];

  vigenciaAbierta = false;

  // El vocabulario, para la plantilla.
  claseDecide = claseDecide;
  iconoDecide = iconoDecide;
  textoDecide = textoDecide;
  sentidoDecide = sentidoDecide;
  nombrePaso = nombrePaso;
  formatoFecha = formatoFecha;

  private subRuta?: Subscription;
  private subComercio?: Subscription;

  constructor(
    private ruta: ActivatedRoute,
    private _catalogo: CatalogoSolicitudesService,
    private _comercioActivo: ComercioActivoService,
  ) { }

  ngOnInit(): void {
    this.subComercio = this._comercioActivo.codigo$.subscribe((codigo) => {
      this.comercioCodigo = codigo;
      this.recalcular();
    });
    this.subRuta = this.ruta.paramMap.subscribe((p) => {
      this.code = (p.get('code') || '').toUpperCase();
      if (this.tipos.length) this.recalcular();
      else this.cargar();
    });
  }

  ngOnDestroy(): void {
    this.subRuta?.unsubscribe();
    this.subComercio?.unsubscribe();
  }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = '';
    try {
      const c: any = await this._catalogo.getCatalogo();
      this.pasos = Array.isArray(c?.pasos) ? c.pasos : [];
      this.tipos = Array.isArray(c?.tipos) ? c.tipos : [];
      this.comercios = Array.isArray(c?.comercios_con_flujo_propio) ? c.comercios_con_flujo_propio : [];
      this.flujoPorOmision = c?.flujo_por_omision || null;
    } catch (e: any) {
      this.tipos = [];
      this.error = e?.error?.msg || 'No se pudo traer el catálogo de tipos y flujos. Intenta de nuevo en un momento.';
    } finally {
      this.recalcular();
      this.cargando = false;
    }
  };

  private recalcular(): void {
    const code = (this.comercioCodigo || '').toUpperCase();
    this.comercioConFlujo = code
      ? (this.comercios.find((m) => String(m?.merchant_code || '').toUpperCase() === code) || null)
      : null;
    this.tipo = this.tipos.find((t) => String(t?.code || '').toUpperCase() === this.code) || null;
    this.recorrido = construirRecorrido(this.tipo, this.pasos, this.comercioConFlujo);
    this.fuera = pasosVisibles(this.pasos).filter((p) => !haceElPaso(p, this.tipo, this.comercioConFlujo));
  }

  get hoy(): string { return formatoFecha(new Date()); }

  get nombreComercio(): string {
    if (!this.comercioCodigo) return '';
    return this.comercioConFlujo?.nombre || this.comercioCodigo;
  }

  /** Los comercios en juego: con uno elegido en la cabecera, sólo ése. */
  private get enJuego(): ComercioFlujo[] {
    if (!this.comercioCodigo) return this.comercios;
    return this.comercioConFlujo ? [this.comercioConFlujo] : [];
  }

  /** ¿Es el paso que cierra la solicitud? Sale de `termina_en`, que lo calcula el servidor: aquí no
   *  se asume que «firmar el pagaré» sea el final — en el link de pago no lo es. */
  esElUltimo = (code: string): boolean => !!this.tipo && this.tipo.termina_en === code;

  /** ¿Este paso es lo que distingue a este tipo de los demás? (`agrega_paso` del catálogo). */
  esElQueDistingue = (code: string): boolean => !!this.tipo && this.tipo.agrega_paso === code;

  /** La configuración del paso en el flujo por omisión. 🔴 Sólo el flujo por omisión la trae: los
   *  `steps` de un comercio vienen con `{code, enabled}` y nada más (medido en
   *  `infra/repos/flujos.js:todosLosVigentes`), así que aquí no se pinta la de nadie más. */
  configDe = (code: string): { clave: string; valor: string }[] => {
    const s = (this.flujoPorOmision?.steps || []).find((x) => x && x.code === code);
    const cfg = s?.config;
    if (!cfg || typeof cfg !== 'object') return [];
    return Object.keys(cfg).map((k) => ({ clave: k.replace(/_/g, ' '), valor: String(cfg[k]) }));
  };

  /** Qué comercios se apartan del recorrido por omisión en ESTE paso — el dato por el que alguien
   *  entra a esta pantalla: «¿lo raro de este comercio es su configuración o es un defecto?». */
  apartanEn = (code: string): { nombre: string; version: string; lo_pide: boolean }[] => {
    const paso = this.pasos.find((p) => p && p.code === code);
    return paso ? comerciosQueSeApartanEn(this.tipo, paso, this.enJuego) : [];
  };

  /** La línea del comercio elegido, sobre ESTE tipo. `''` cuando se están mirando todos. */
  get lineaComercio(): string {
    if (!this.comercioCodigo) return '';
    const m = this.comercioConFlujo;
    if (!m) return `${this.nombreComercio} no tiene flujo propio: hace el recorrido por omisión.`;
    const version = `${m.version_code || 'V?'} · desde ${formatoFecha(m.desde)}`;
    if (!seAparta(this.tipo, this.pasos, m)) {
      return `${this.nombreComercio} tiene flujo propio (${version}), pero en este tipo hace lo mismo que el recorrido por omisión.`;
    }
    return `${this.nombreComercio} (${version}) se aparta del recorrido por omisión: `
      + `${diferenciaEnPalabras(this.tipo, this.pasos, m)}.`;
  }
}
